import { useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type {
  ContactFormLeadTriggerObject,
  ContactFormTriggerLocation,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { ContactForm } from './ContactForm';
import { useHaloContactSubmission } from './useHaloContactSubmission';
import type { ContactFormMode } from './types';
import * as spokeStyles from './contactFormSpoke.styles';
import * as formStyles from './contactForm.styles';

type ContactFormSpokeProps = {
  onClose: () => void;
  listing?: ListingDetails;
  mode?: ContactFormMode;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
};

export const ContactFormSpoke = ({
  onClose,
  listing: listingProp,
  mode = 'contact',
  triggerLocation,
  triggerObject,
}: ContactFormSpokeProps) => {
  const reduxListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const user = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCache = useSelector((state: RootReduxState) => state.user?.inquiryDataCache);

  const listing = listingProp || reduxListing;

  if (!listing) {
    return null;
  }

  return (
    <ContactFormSpokeInner
      listing={listing}
      user={user}
      userInquiryDataCache={userInquiryDataCache}
      onClose={onClose}
      mode={mode}
      triggerLocation={triggerLocation}
      triggerObject={triggerObject}
    />
  );
};

type ContactFormSpokeInnerProps = {
  listing: ListingDetails;
  user: RootReduxState['user'];
  userInquiryDataCache: RootReduxState['user']['inquiryDataCache'];
  onClose: () => void;
  mode: ContactFormMode;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
};

const ContactFormSpokeInner = ({
  listing,
  user,
  userInquiryDataCache,
  onClose,
  mode,
  triggerLocation,
  triggerObject,
}: ContactFormSpokeInnerProps) => {
  const { handleSubmitForm, isLoading, serverErrors } = useHaloContactSubmission({
    listing,
    onClose,
    requestToApply: mode === 'requestToApply',
    triggerLocation,
    triggerObject,
  });
  const allPhotos = listing?.largePhotos?.length ? listing.largePhotos : listing?.photos || [];
  const previewPhoto = listing?.previewPhoto?.url ? [{ url: listing.previewPhoto.url }] : [];
  const displayPhotos = allPhotos.length > 0 ? allPhotos.slice(0, 4) : previewPhoto;

  return (
    <div className={spokeStyles.spokeFormWrapper}>
      {displayPhotos.length > 0 && (
        <div className={formStyles.photoGrid}>
          {displayPhotos.map((photo, index) => (
            <img
              key={photo.url || index}
              src={photo.url}
              alt={index === 0 ? listing?.displayName || 'Listing photo' : ''}
              className={cx(formStyles.photoGridImage, displayPhotos.length === 1 && formStyles.photoGridImageSingle)}
            />
          ))}
        </div>
      )}
      {listing?.displayName && <p className={formStyles.listingName}>{listing.displayName}</p>}
      <div className={spokeStyles.spokeFormContent}>
        <ContactForm
          listing={listing}
          onSubmit={handleSubmitForm}
          isLoading={isLoading}
          serverErrors={serverErrors}
          userInquiryDataCache={userInquiryDataCache}
          user={user}
          mode={mode}
        />
      </div>
      {isLoading && (
        <div className={spokeStyles.loadingOverlay} aria-hidden="true">
          <div className={spokeStyles.loadingSpinner} />
        </div>
      )}
    </div>
  );
};
